import { createSlice } from "@reduxjs/toolkit";
import { isPWAInstalled } from "../../utils/index.js";
import { isMobileOrTablet } from "../../utils/isMobileOrTablet.js";
/**
 * Determines if the app is running as a mobile PWA or native app wrapper.
 *
 * Uses isMobileOrTablet() (UA-based) instead of isOnSmallScreen() (viewport-based)
 * so that a native app launching in landscape is correctly detected —
 * a phone is still a phone regardless of orientation.
 *
 * @returns {boolean}
 */
function getMobileApp() {
  // Use minimum dimension (portrait width) so landscape launch doesn't break detection.
  // A phone's smallest dimension is always <= 767px regardless of orientation.
  const minDimension = Math.min(window.innerWidth, window.innerHeight);
  return minDimension <= 767 && isPWAInstalled();
}

function getMobileOrTabletApp() {
  return isMobileOrTablet() && isPWAInstalled();
}

/**
 * Runtime details slice for storing app runtime state
 *
 * Note: isMobileApp is set once at initialization and does not dynamically update
 * on window resize. The responsive website will still function correctly at smaller sizes.
 */
const runtimeDetails = createSlice({
  name: "runtimeDetails",
  initialState: {
    isMobileApp: getMobileApp(),
    isMobileOrTabletApp: getMobileOrTabletApp(),
  },
});

/**
 * Selector to get mobile app status from Redux state
 *
 * @param {object} state - Redux state
 * @returns {boolean} True if running as mobile app
 */
export const selectIsMobileApp = (state) => state.runtimeDetails.isMobileApp;
export const selectIsMobileOrTabletApp = (state) => state.runtimeDetails.isMobileOrTabletApp;

export default runtimeDetails.reducer;
